import type { AgentConfigurationScope } from "@app/types/assistant/agent";
import SUPPORTED_MODEL_CONFIGS from "@app/types/assistant/models/models";
import { O4_MINI_MODEL_ID } from "@app/types/assistant/models/openai";
import type {
  ModelConfigurationType,
  ModelIdType,
  ModelProviderIdType,
  SupportedModel,
} from "@app/types/assistant/models/types";

export interface PrefetchedWhitelistedModels {
  small: ModelConfigurationType | null;
  large: ModelConfigurationType | null;
  whitelistedProviders: Set<ModelProviderIdType>;
}

export const DEFAULT_REASONING_MODEL_ID = O4_MINI_MODEL_ID;

export const DEFAULT_TOKEN_COUNT_ADJUSTMENT = 1.15;

export function isSupportedModel(
  model: unknown,
  checkProvider: boolean = true
): model is SupportedModel {
  if (
    typeof model !== "object" ||
    model === null ||
    !("modelId" in model) ||
    typeof model.modelId !== "string"
  ) {
    return false;
  }

  return SUPPORTED_MODEL_CONFIGS.some(
    (m) =>
      m.modelId === model.modelId &&
      (!checkProvider ||
        ("providerId" in model && m.providerId === model.providerId))
  );
}

export function isSupportingResponseFormat(modelId: ModelIdType) {
  const model = SUPPORTED_MODEL_CONFIGS.find(
    (config) => config.modelId === modelId
  );
  return model?.supportsResponseFormat;
}

/**
 * Global agent list (stored here to be imported from client-side)
 */

export enum GLOBAL_AGENTS_SID {
  HELPER = "helper",
  DUST = "dust",
  DUST_OMITTED = "dust-omitted",
  DUST_HIGH = "dust-high",
  DUST_HIGH_OMITTED = "dust-high-omitted",
  DUST_EDGE = "dust-edge",
  DUST_QUICK = "dust-quick",
  DUST_QUICK_MEDIUM = "dust-quick-medium",
  DUST_OAI = "dust-oai",
  DUST_OAI_MEDIUM = "dust-oai-medium",
  DUST_OAI_HIGH = "dust-oai-high",
  DUST_OAI_LUNA = "dust-oai-luna",
  DUST_OAI_LUNA_MEDIUM = "dust-oai-luna-medium",
  DUST_OAI_LUNA_HIGH = "dust-oai-luna-high",
  DUST_OAI_NANO_HIGH = "dust-oai-nano-high",
  DUST_GOOG = "dust-goog",
  DUST_GOOG_MEDIUM = "dust-goog-medium",
  DUST_GOOG_HIGH = "dust-goog-high",
  DUST_GOOG_LITE = "dust-goog-lite",
  DUST_GOOG_PRO = "dust-goog-pro",
  DUST_GOOG_PRO_MEDIUM = "dust-goog-pro-medium",
  DUST_GOOG_PRO_HIGH = "dust-goog-pro-high",
  DUST_NEXT = "dust-next",
  DUST_NEXT_MEDIUM = "dust-next-medium",
  DUST_CHAWI = "dust-chawi",
  DUST_CHAWI_MEDIUM = "dust-chawi-medium",
  DUST_CHAWI_HIGH = "dust-chawi-high",
  DUST_SOUPINOU = "dust-soupinou",
  DUST_SOUPINOU_MEDIUM = "dust-soupinou-medium",
  DUST_SOUPINOU_HIGH = "dust-soupinou-high",
  DUST_SOUPINOU_NONE = "dust-soupinou-none",
  DUST_SUNDAE = "dust-sundae",
  DUST_SUNDAE_MEDIUM = "dust-sundae-medium",
  DUST_SUNDAE_HIGH = "dust-sundae-high",
  DUST_PISTACHE = "dust-pistache",
  DUST_PISTACHE_MEDIUM = "dust-pistache-medium",
  DUST_PISTACHE_HIGH = "dust-pistache-high",
  DUST_CHALOM = "dust-chalom",
  DUST_CHALOM_MEDIUM = "dust-chalom-medium",
  DUST_CHALOM_HIGH = "dust-chalom-high",
  DUST_LIONEL = "dust-lionel",
  DUST_LIONEL_MEDIUM = "dust-lionel-medium",
  DUST_LIONEL_HIGH = "dust-lionel-high",
  DUST_ANT = "dust-ant",
  DUST_ANT_MEDIUM = "dust-ant-medium",
  DUST_ANT_HIGH = "dust-ant-high",
  DUST_ANT_MEDIUM_OMITTED = "dust-ant-medium-omitted",
  DUST_ANT_HIGH_OMITTED = "dust-ant-high-omitted",
  DUST_ANT_SONNET_EDGE = "dust-ant-sonnet-edge",
  DUST_ANT_SONNET_EDGE_LIGHT = "dust-ant-sonnet-edge-light",
  DUST_HAIKU = "dust-haiku",
  DUST_LIGHT = "dust-light",
  DUST_KIMI = "dust-kimi",
  DUST_KIMI_MEDIUM = "dust-kimi-medium",
  DUST_KIMI_HIGH = "dust-kimi-high",
  DUST_GLM = "dust-glm",
  DUST_GLM_MEDIUM = "dust-glm-medium",
  DUST_GLM_HIGH = "dust-glm-high",
  DUST_MINIMAX = "dust-minimax",
  DUST_MINIMAX_MEDIUM = "dust-minimax-medium",
  DUST_MINIMAX_HIGH = "dust-minimax-high",
  DUST_DEEPSEEK = "dust-deepseek",
  DUST_MISTRAL_MEDIUM_NONE = "dust-mistral-medium-none",
  DUST_MISTRAL_MEDIUM_HIGH = "dust-mistral-medium-high",
  DUST_NEXT_HIGH = "dust-next-high",
  DEEP_DIVE = "deep-dive",
  // DASIS: prebuilt Korean e-tax-invoice agent backed by the bolta MCP server.
  TAX_INVOICE = "tax-invoice",
  DUST_TASK = "dust-task",
  DUST_BROWSER_SUMMARY = "dust-browser-summary",
  DUST_PLANNING = "dust-planning",
  SIDEKICK = "sidekick",
  REINFORCEMENT = "reinforcement",
  SLACK = "slack",
  GOOGLE_DRIVE = "google_drive",
  NOTION = "notion",
  GITHUB = "github",
  INTERCOM = "intercom",
  GPT35_TURBO = "gpt-3.5-turbo",
  GPT4 = "gpt-4",
  GPT5 = "gpt-5",
  GPT5_THINKING = "gpt-5-thinking",
  GPT5_NANO = "gpt-5-nano",
  GPT5_MINI = "gpt-5-mini",
  O1 = "o1",
  O1_MINI = "o1-mini",
  O1_HIGH_REASONING = "o1_high",
  O3_MINI = "o3-mini",
  O3 = "o3",
  CLAUDE_4_5_HAIKU = "claude-4.5-haiku",
  CLAUDE_5_SONNET = "claude-5-sonnet",
  CLAUDE_4_5_SONNET = "claude-4.5-sonnet",
  CLAUDE_4_SONNET = "claude-4-sonnet",
  CLAUDE_3_OPUS = "claude-3-opus",
  CLAUDE_3_SONNET = "claude-3-sonnet",
  CLAUDE_3_HAIKU = "claude-3-haiku",
  CLAUDE_3_7_SONNET = "claude-3-7-sonnet",
  MISTRAL_LARGE = "mistral-large",
  MISTRAL_MEDIUM = "mistral-medium",
  //!\ TEMPORARY WORKAROUND: Renaming 'mistral' to 'mistral-small' is not feasible since
  // it interferes with the retrieval of ongoing conversations involving this agent.
  // Needed to preserve ongoing chat integrity due to 'sId=mistral' references in legacy messages.
  MISTRAL_SMALL = "mistral",
  GEMINI_PRO = "gemini-pro",

  ANALYST = "analyst",

  NOOP = "noop",
}

export function isGlobalAgentId(sId: string): sId is GLOBAL_AGENTS_SID {
  return (Object.values(GLOBAL_AGENTS_SID) as string[]).includes(sId);
}

// Hidden helper sub-agents that are only ever invoked internally (e.g. via
// run_agent by the Deep Dive agent). On their own they are not meaningful to
// users, so usage they generate should be attributed to the parent agent that
// spawned them rather than to the helper itself. Other sub-agents (real user
// agents invoked via run_agent / agent_handover) keep their own attribution.
export const HIDDEN_HELPER_SUB_AGENT_SIDS: ReadonlySet<string> =
  new Set<string>([
    GLOBAL_AGENTS_SID.DUST_TASK,
    GLOBAL_AGENTS_SID.DUST_PLANNING,
    GLOBAL_AGENTS_SID.DUST_BROWSER_SUMMARY,
  ]);

export function isHiddenHelperSubAgentId(sId: string): boolean {
  return HIDDEN_HELPER_SUB_AGENT_SIDS.has(sId);
}

// If you want to show feedback buttons for global agents, add sId here.
const GLOBAL_AGENTS_WITH_FEEDBACK = new Set<GLOBAL_AGENTS_SID>([
  GLOBAL_AGENTS_SID.DUST,
  GLOBAL_AGENTS_SID.DEEP_DIVE,
  GLOBAL_AGENTS_SID.SIDEKICK,
]);

export function isGlobalAgentWithFeedback(sId: GLOBAL_AGENTS_SID): boolean {
  return GLOBAL_AGENTS_WITH_FEEDBACK.has(sId);
}

const AGENT_IDS_RESTRICTED_TO_BUILDER = new Set<string>([
  GLOBAL_AGENTS_SID.SIDEKICK,
]);

export function canShowAgentConversationActions(agentId: string): boolean {
  return !AGENT_IDS_RESTRICTED_TO_BUILDER.has(agentId);
}

export function getGlobalAgentAuthorName(agentId: string): string {
  switch (agentId) {
    case GLOBAL_AGENTS_SID.GPT5:
    case GLOBAL_AGENTS_SID.GPT5_THINKING:
    case GLOBAL_AGENTS_SID.GPT5_NANO:
    case GLOBAL_AGENTS_SID.GPT5_MINI:
    case GLOBAL_AGENTS_SID.O1:
    case GLOBAL_AGENTS_SID.O1_MINI:
    case GLOBAL_AGENTS_SID.O1_HIGH_REASONING:
    case GLOBAL_AGENTS_SID.O3_MINI:
    case GLOBAL_AGENTS_SID.O3:
      return "OpenAI";
    case GLOBAL_AGENTS_SID.CLAUDE_5_SONNET:
    case GLOBAL_AGENTS_SID.CLAUDE_4_SONNET:
    case GLOBAL_AGENTS_SID.CLAUDE_4_5_SONNET:
    case GLOBAL_AGENTS_SID.CLAUDE_4_5_HAIKU:
      return "Anthropic";
    case GLOBAL_AGENTS_SID.MISTRAL_LARGE:
      return "Mistral";
    case GLOBAL_AGENTS_SID.GEMINI_PRO:
      return "Google";
    case GLOBAL_AGENTS_SID.NOOP:
      return "Noop";
    default:
      return "Dasis";
  }
}

// Not exhaustive.
const GLOBAL_AGENTS_SORT_ORDER: string[] = [
  GLOBAL_AGENTS_SID.DUST,
  GLOBAL_AGENTS_SID.DEEP_DIVE,
  GLOBAL_AGENTS_SID.CLAUDE_5_SONNET,
  GLOBAL_AGENTS_SID.GPT5,
  GLOBAL_AGENTS_SID.GEMINI_PRO,
  GLOBAL_AGENTS_SID.MISTRAL_LARGE,
  GLOBAL_AGENTS_SID.CLAUDE_4_5_HAIKU,
  GLOBAL_AGENTS_SID.GPT5_MINI,
  GLOBAL_AGENTS_SID.GPT4,
  GLOBAL_AGENTS_SID.DUST_EDGE,
  GLOBAL_AGENTS_SID.DUST_QUICK,
  GLOBAL_AGENTS_SID.DUST_OAI,
  GLOBAL_AGENTS_SID.DUST_OAI_MEDIUM,
  GLOBAL_AGENTS_SID.DUST_OAI_HIGH,
  GLOBAL_AGENTS_SID.DUST_OAI_LUNA,
  GLOBAL_AGENTS_SID.DUST_OAI_LUNA_MEDIUM,
  GLOBAL_AGENTS_SID.DUST_OAI_LUNA_HIGH,
  GLOBAL_AGENTS_SID.DUST_CHAWI,
  GLOBAL_AGENTS_SID.DUST_CHAWI_MEDIUM,
  GLOBAL_AGENTS_SID.DUST_CHAWI_HIGH,
  GLOBAL_AGENTS_SID.DUST_SOUPINOU,
  GLOBAL_AGENTS_SID.DUST_SOUPINOU_MEDIUM,
  GLOBAL_AGENTS_SID.DUST_SOUPINOU_HIGH,
  GLOBAL_AGENTS_SID.DUST_SOUPINOU_NONE,
  GLOBAL_AGENTS_SID.DUST_SUNDAE,
  GLOBAL_AGENTS_SID.DUST_SUNDAE_MEDIUM,
  GLOBAL_AGENTS_SID.DUST_SUNDAE_HIGH,
  GLOBAL_AGENTS_SID.DUST_PISTACHE,
  GLOBAL_AGENTS_SID.DUST_PISTACHE_MEDIUM,
  GLOBAL_AGENTS_SID.DUST_PISTACHE_HIGH,
  GLOBAL_AGENTS_SID.DUST_CHALOM,
  GLOBAL_AGENTS_SID.DUST_CHALOM_MEDIUM,
  GLOBAL_AGENTS_SID.DUST_CHALOM_HIGH,
  GLOBAL_AGENTS_SID.DUST_LIONEL,
  GLOBAL_AGENTS_SID.DUST_LIONEL_MEDIUM,
  GLOBAL_AGENTS_SID.DUST_LIONEL_HIGH,
];
const globalAgentIndexMap = new Map(
  GLOBAL_AGENTS_SORT_ORDER.map((id, index) => [id, index])
);

// This function implements our general strategy to sort agents to users (input bar, agent list,
// agent suggestions...).
export function compareAgentsForSort<
  T extends {
    sId: string;
    userFavorite: boolean | undefined;
    scope: AgentConfigurationScope;
    name: string;
  },
>(a: T, b: T): number {
  if (a.userFavorite && !b.userFavorite) {
    return -1;
  }
  if (b.userFavorite && !a.userFavorite) {
    return 1;
  }

  const aGlobalIndex = globalAgentIndexMap.get(a.sId) ?? -1;
  const bGlobalIndex = globalAgentIndexMap.get(b.sId) ?? -1;

  if (aGlobalIndex !== -1 && bGlobalIndex !== -1) {
    return aGlobalIndex - bGlobalIndex;
  }
  if (aGlobalIndex !== -1) {
    return -1;
  }
  if (bGlobalIndex !== -1) {
    return 1;
  }

  // Check for agents with non-global 'scope'
  if (a.scope !== "global" && b.scope === "global") {
    return -1;
  }
  if (b.scope !== "global" && a.scope === "global") {
    return 1;
  }

  // default: sort alphabetically
  return a.name.localeCompare(b.name, "en", { sensitivity: "base" });
}
